<template>
  <div style="overflow-y: auto; scrollbar-gutter: stable; max-height: 600px;">
    <div v-if="!sensors?.length" class="step-input">
      <p>No sensors found. Please add a sensor.</p>
    </div>
    <div v-for="(sensor, index) in sensors" :key="index">
      <div v-if="!sensor.deleted" class="sensor-section"
           :class="{'validation-failure': failedValidationSensors[index]}">
        <div class="sensor-header d-flex justify-content-between align-items-center">
          <h5>{{ sensorName(sensor) }}</h5>

          <div class="button-group d-flex">
            <button class="btn btn-sm btn-outline-secondary" @click="toggleSensor(index)">
              {{ sensor.collapsed ? 'Show' : 'Hide' }} Details
            </button>
            <button class="btn btn-sm ms-2" id="deleteSensorBtn" title="Delete Sensor" @click="removeSensor(index)">
              <i class="bi bi-trash" style="font-size:1.1em"></i>
            </button>
          </div>
        </div>

        <!-- Collapsible Sensor Attribute List -->
        <div v-show="!sensor.collapsed">
          <div class="row attr-row">
            <div class="col-md-4">
              <label> {{ getAttribute(index, 9151)?.attribute_pretty_name }} </label> <!-- Sensor Model (9151) -->
            </div>
            <div class="col-md-8">
              <select
                  @input="updateSensorType(index, $event.target.value)"
                  :value="getAttribute(index, 9151).attribute_value"
                  class="form-control-sm attr-input text-center">
                <option v-for="option in sensorModels" :value="option.sensor_model_id" :key="option">
                  {{ option.sensor_name }} - {{ option.sensor_type_name }}
                </option>
              </select>
            </div>
          </div>
          <div class="row attr-row">
            <div class="col-md-4">
              <label> {{ getAttribute(index, 9150).attribute_pretty_name }} </label> <!-- Sensor Name (9150) -->
            </div>
            <div class="col-md-8">
              <input class="form-control-sm attr-input"
                     type="text"
                     :value="getAttribute(index, 9150).attribute_value"
                     @input="updateAttribute(index, 9150, $event.target.value)"
              />
            </div>
          </div>
          <div v-if="isPowerLine(index)">
            <div class="row attr-row">
              <div class="col-md-4">
                <label> {{ getAttribute(index, 9153).attribute_pretty_name }} </label> <!-- Power Line (9153) -->
              </div>
              <div class="col-md-8">
                <input class="form-control-sm attr-input"
                       type="number"
                       :min="getAttribute(index, 9153).min"
                       :max="getAttribute(index, 9153).max"
                       :value="getAttribute(index, 9153).attribute_value"
                       @input="updateAttribute(index, 9153, $event.target.value)"
                />
              </div>
            </div>
            <div class="row attr-row" v-if="is4to20(index)">
              <div class="col-md-12 text-center text-muted">
                <small>For 4-20mA sensors, the power line must be between 1 and 4.</small>
              </div>
            </div>
          </div>
          <div v-if="isModbus(index)">
            <div class="row attr-row">
              <div class="col-md-4">
                <label> Modbus ID </label>
              </div>
              <div class="col-md-8">
                <input class="form-control-sm attr-input"
                       type="number"
                       :min="1"
                       :max="250"
                       :value="sensor.modbus_id"
                       @input="updateModbusId(index, $event.target.value)"
                />
              </div>
            </div>
            <div class="row attr-row" v-if="!isOther(index)">
              <div class="col-md-4">
                <label> Reading Registers </label>
              </div>
              <div class="col-md-8">
                <Multiselect
                    :key="`ms-${index}-${getAttribute(index, 9151)?.attribute_value || 'none'}`"
                    mode="multiple"
                  @change="(ids) => updateSensorRegisters(index, registersFromIds(index, ids))"
                  :model-value="selectedRegisters(index).map(r => r.id)"
                  :options="readingRegisters(sensor.attributes.find(a => a.attribute_info_id === 9151)?.attribute_value)"
                  label="address"
                  track-by="id"
                  value-prop="id"
                  :multiple="true"
                  :can-clear="false"
                  :close-on-select="false"
                  :close-on-deselect="false"
                  :clear-on-select="false"
                  :clear-on-deselect="false"
                  :searchable="false"
                  :show-labels="false"
                  :hide-selected="false"
                  :object="false"
                  placeholder="true"
                >
                  <!-- Slot for the collapsed selection view -->
                  <template #placeholder>
                    <span style="cursor: pointer; text-align:center; flex-grow: 1">
                      0 addresses selected
                    </span>
                  </template>
                  <template #multiplelabel="{ values }">
                    <span style="cursor: pointer; text-align:center; flex-grow: 1">
                      {{ values && values.length > 0 ? values.length : '0' }} addresses selected
                    </span>
                  </template>
                  <template #option="{ option, isSelected }">
                    <div class="d-flex align-items-center option-item">
                      <i v-if="isSelected(option)" class="bi bi-check custom-checkmark"/>
                      {{ option?.address }} - {{ option?.description }}
                    </div>
                  </template>
                </Multiselect>
              </div>
            </div>
            <div v-else>
              <div class="row attr-row">
                <div class="col-md-4">
                  <label> Reading Registers </label>
                </div>
                <div class="col-md-8">
                  <OtherModbusList :sensorIdx="index"/>
                </div>
              </div>
            </div>
            <div class="row attr-row">
              <div class="col-md-4">
                <label> {{ getAttribute(index, 9146)?.attribute_pretty_name }} </label> <!-- Default Register (9146) -->
              </div>
              <div class="col-md-8">
                <select
                    :value="getAttribute(index, 9146).attribute_value"
                    @change="updateAttribute(index, 9146, $event.target.value)"
                    class="form-control-sm text-center attr-input">
                  <option v-if="selectedRegisters(index)?.filter(r => r.address != null)?.length === 0" disabled value="">
                    Please select registers to read
                  </option>
                  <option v-else v-for="option in sortedSelectedRegisters(index)" :value="option.address" :key="option.address">
                    {{ option.address }} - {{ option.description }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Multiselect from "@vueform/multiselect";
import {computed, ref} from "vue";
import {useStore} from "vuex";
import OtherModbusList from "@/components/shared/lists/other-modbus-list.vue";

const store = useStore();
const failedValidationSensors = ref([]);


const validate = () => {
  failedValidationSensors.value = [];
  if (!sensors.value?.length || sensors.value.every(s => s.deleted)) {
    return {valid: false, msg: 'No sensors found. Please add a sensor.'};
  }
  for (let i = 0; i < sensors.value.length; i++) {
    if (store.getters['ConfigWizard/isDeleted'](i)) {
      continue;
    }

    // every sensor must have a type
    const sensorType = getAttribute(i, 9151).attribute_value;
    if (sensorType == null || sensorType === '') {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please select a sensor type for each sensor.'};
    }

    // every sensor must have a name
    const sensorNameAttr = getAttribute(i, 9150).attribute_value;
    if (sensorNameAttr == null || sensorNameAttr === '') {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please enter a name for each sensor.'};
    }

    // modbus sensors must have a modbus id
    if (isModbus(i) && (sensors.value[i].modbus_id == null || sensors.value[i].modbus_id === '')) {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please enter a Modbus ID for each Modbus sensor.'};
    }
    else if (isModbus(i) && (sensors.value[i].modbus_id <= 0 || sensors.value[i].modbus_id >= 251)) {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please enter a Modbus ID between 1 and 250'};
    }

    // modbus sensors must have a default address
    if (isModbus(i) && (getAttribute(i, 9146).attribute_value == null || getAttribute(i, 9146).attribute_value === '')) {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please select a default address for each Modbus sensor.'};
    }
    // power line sensors must have a power line value
    if (isPowerLine(i)) {
      const powerLineVal = getAttribute(i, 9153).attribute_value;
      if (powerLineVal == null || powerLineVal === '') {
        failedValidationSensors.value[i] = true;
        return {valid: false, msg: 'Please set powerline.'};
      }
      const val = parseFloat(powerLineVal);
      if (is4to20(i)) {
        if (isNaN(val) || val < 1 || val > 4) {
          failedValidationSensors.value[i] = true;
          return {valid: false, msg: 'Power line for 4-20mA sensors must be between 1 and 4.'};
        }
      } else {
        const min = getAttribute(i, 9153).min || 1;
        const max = getAttribute(i, 9153).max || 6;
        if (isNaN(val) || val < min || val > max) {
          failedValidationSensors.value[i] = true;
          return {valid: false, msg: `Power line must be between ${min} and ${max}.`};
        }
      }
    }
    // all modbus registers must have all fields filled out
    if (isModbus(i)) {
      for (let reg of selectedRegisters(i)) {
        if ((reg.address == null || reg.quantity == null || reg.application_sensor_type_id == null) && !isOther(i)) { //see other-modbus-list.vue:27
          failedValidationSensors.value[i] = true;
          return {valid: false, msg: 'Please fill out all fields for each register.'};
        }
      }
    }

    // all modbus sensors must have at least one register
    if (isModbus(i) && selectedRegisters(i).length === 0) {
      failedValidationSensors.value[i] = true;
      return {valid: false, msg: 'Please select at least one register for each Modbus sensor.'};
    }

    // we're good if we made it here
    failedValidationSensors.value[i] = false;
  }
  // return success
  return {valid: true, msg: ''};
}

const save = async () => {
  // save all the sensors
  return await store.dispatch('ConfigWizard/saveSensors');
}

const fetch = async () => {
  // After saving, we need to reload the gateway to get the new sensors
  let node = store.getters['MyView/getGateway'](store.state.gateway.id);
  await store.dispatch('MyView/clickGateway', {node});
}

// eslint-disable-next-line no-undef
defineExpose({validate, save, fetch});

const sensors = computed(() => store.state.ConfigWizard.sensors);
const sensorModels = computed(() => store.state.ConfigWizard.sensorModels);
const getAttribute = (sensorIdx, attr_info_id) => store.getters['ConfigWizard/getAttribute'](sensorIdx, attr_info_id);
const toggleSensor = (index) => store.commit('ConfigWizard/toggleCollapse', index);
const removeSensor = (index) => store.commit('ConfigWizard/deleteSensor', index);
const readingRegisters = (sensorModel) => store.getters['ConfigWizard/registersForModel'](sensorModel);
const updateAttribute = (sensorIdx, attrInfoId, value) => {
  // Determine if the value should be treated as a number
  const isNumeric = value !== '' && value != null && !isNaN(value) && !(typeof value === 'string' && value.trim() === '');

  const val = isNumeric ? parseFloat(value) : (value === '' ? null : value);

  store.commit('ConfigWizard/updateSensorAttribute', { sensorIdx, attrInfoId, value: val });
}
const updateModbusId = (sensorIdx, value) => store.commit('ConfigWizard/updateSensorModbusId', { sensorIdx, value: value === '' ? null : parseFloat(value) });
// A model can define more than one reading register at the same address (raw reading vs volume for 299)
const registerKey = (reg) => reg.id ?? reg.address;
const isVolumeRegister = (reg) => reg?.description === 'Volume';

// The multiselect works with ids. With objects it writes labels back onto the store's registers by position, which swaps
// their addresses once the click order differs from the stored order
const registersFromIds = (sensorIdx, ids) => {
  const options = readingRegisters(getAttribute(sensorIdx, 9151)?.attribute_value);
  return (ids || []).map(id => options.find(o => o.id == id)).filter(Boolean);
};

const updateSensorRegisters = (sensorIdx, value) => {
  let newRegisters = Array.isArray(value) ? [...value] : [];
  const currentRegisters = store.state.ConfigWizard.sensors[sensorIdx].registers || [];

  const previouslySelectedKeys = new Set(
    currentRegisters.filter(r => r.address != null && !r.deleted).map(registerKey)
  );

  // Only one register per address: picking Volume replaces the raw distance at 299 (1 on radars), and the other way around
  const newlyAdded = newRegisters.filter(reg => !previouslySelectedKeys.has(registerKey(reg)));
  for (const added of newlyAdded) {
    newRegisters = newRegisters.filter(reg => registerKey(reg) === registerKey(added) || reg.address !== added.address);
  }

  const newRegMap = new Map(newRegisters.map(reg => [registerKey(reg), reg]));

  const updatedRegisters = currentRegisters.map(reg => {
    if (reg.address == null) return reg;
    const isStillSelected = newRegMap.has(registerKey(reg));
    if (!isStillSelected) {
      return { ...reg, deleted: true };
    }
    return { ...reg, ...newRegMap.get(registerKey(reg)), deleted: false };
  });

  newRegisters.forEach(newReg => {
    if (newReg.address == null) return;
    const exists = updatedRegisters.some(reg => registerKey(reg) === registerKey(newReg));
    if (!exists) {
      updatedRegisters.push({ ...newReg, deleted: false });
    }
  });

  if (newRegisters.length === 0) {  // deselection
    updateAttribute(sensorIdx, 9146, null);
  } else {
    const currentDefault = getAttribute(sensorIdx, 9146).attribute_value;
    if (currentDefault != null) {
      const currentDefaultStr = currentDefault.toString();
      const isStillPresent = newRegisters.some(reg => 
        reg.address != null && (reg.address === currentDefault || reg.address.toString() === currentDefaultStr)
      );
      if (!isStillPresent) {
        updateAttribute(sensorIdx, 9146, null);
      }
    }
  }
  // A saved tank config is meaningless once the sensor stops reading Volume
  const hadVolume = currentRegisters.some(r => r.address != null && !r.deleted && isVolumeRegister(r));
  const stillHasVolume = newRegisters.some(isVolumeRegister);
  if (hadVolume && !stillHasVolume) {
    store.commit('ConfigWizard/clearSensorTankConfig', sensorIdx);
  }

  store.commit('ConfigWizard/updateSensorRegisters', { sensorIdx, registers: updatedRegisters });
}

const selectedRegisters = (sensorIdx) => store.getters['ConfigWizard/selectedRegisters'](sensorIdx);
// Registers are stored in the order they were first picked, so the default register list is sorted by address
const sortedSelectedRegisters = (sensorIdx) => [...selectedRegisters(sensorIdx)].sort((a, b) => a.address - b.address);
const isOther = (sensorIdx) => store.getters['ConfigWizard/isOther'](sensorIdx);
const updateSensorType = (sensorIdx, type_id) => store.dispatch('ConfigWizard/updateSensorType', { sensorIdx, type_id });
const isModbus = (sensorIdx) => store.getters['ConfigWizard/isModbus'](sensorIdx);
const isPowerLine = (sensorIdx) => store.getters['ConfigWizard/isPowerLine'](sensorIdx);
const is4to20 = (sensorIdx) => store.getters['ConfigWizard/sensorProtocolTypeId'](sensorIdx) === 3; // Protocol Type 3 is 4-20mA
const sensorName = (sensor) => {
  // Attribute 9150 is the Sensor Name
  let name = sensor.attributes.find(a => a.attribute_info_id === 9150)?.attribute_value;
  if (name != null) {
    return name;
  }
  if (sensor.sensor_type_name == null || sensor.modbus_id == null) {
    return 'Unnamed Sensor';
  }
  return `${sensor.sensor_type_name} ${sensor.modbus_id}`;
}
</script>

<style scoped>
.sensor-section {
  margin-bottom: 20px;
  padding: 10px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background-color: var(--color-bg);
}

.sensor-header h5 {
  margin: 0;
}

#deleteSensorBtn:hover {
  background-color: red!important;
  border-color: red!important;
}

.sensor-header {
  position: relative;
  margin-bottom: 10px;
}

.attr-row {
  white-space: nowrap;
  background-color: var(--color-bg);
}

.attr-input {
  text-align: center;
  width: 100%;
  background-color: var(--color-bg-sec);
}

select.attr-input {
  cursor: pointer;
}

.validation-failure {
  border: 2px solid red;
}
</style>